import { useEffect, useState } from 'react';
import { getAdblockState, AdblockDetectionResult } from '../../scripts/adblockDetector';

/**
 * Hook to detect adblock status.
 *
 * Returns tiered detection state:
 * - isBlocked (confirmed): provider signal OR 2+ independent signals
 *   Safe for destructive actions (zone collapse, content hiding)
 * - isSuspected: 1+ signals fired
 *   Safe for non-destructive actions (modal prompt)
 *
 * Uses closure-scoped state from the detector module, which cannot be
 * easily tampered with via console commands.
 */
export function useAdblockDetection(): {
  isBlocked: boolean | null;
  isSuspected: boolean | null;
  isReady: boolean;
} {
  const [result, setResult] = useState<AdblockDetectionResult | null>(() => getAdblockState());

  useEffect(() => {
    // If already detected, sync state
    const currentState = getAdblockState();
    if (currentState !== null) {
      setResult(currentState);
      return;
    }

    const handleDetection = () => {
      // Always read from the closure-scoped getter, not from event.detail
      // This prevents bypass via fake event dispatch
      setResult(getAdblockState());
    };

    window.addEventListener('rio:adblock-detection-complete', handleDetection);

    return () => {
      window.removeEventListener('rio:adblock-detection-complete', handleDetection);
    };
  }, []);

  return {
    isBlocked: result ? result.confirmed : null,
    isSuspected: result ? result.suspected : null,
    isReady: result !== null
  };
}
